import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';

import { useApi } from 'landing-page/hooks/useApi';
import { api, ImpactResponseType } from 'landing-page/helpers/api';

import Container from '../../common/Container';
import SectionHeader from '../SectionHeader';

// Placeholder component for Impact cards
const ImpactPlaceholder: React.FC = () => {
    const { Body } = Card;
    
    return (
        <Container>
            <SectionHeader
                autoFetch={true}
                dataType="header_6"
                withTitleAnimation={true}
                withDescriptionAnimation={false}
            />

            <div className='row d-flex align-items-center justify-content-start flex-wrap'>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                    <div key={index} className='col-md-3'>
                        <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                            <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                                <div className='d-flex align-items-center justify-content-center w-25'>
                                    <div className='placeholder-glow'>
                                        <div className='placeholder bg-primary rounded-circle' style={{ height: '60px', width: '60px' }}></div>
                                    </div>
                                </div>
                                <div className='w-75'>
                                    <div className='placeholder-glow mb-2'>
                                        <div className='placeholder bg-secondary rounded' style={{ height: '24px', width: '60px' }}></div>
                                    </div>
                                    <div className='placeholder-glow'>
                                        <div className='placeholder bg-secondary rounded' style={{ height: '16px', width: '100%' }}></div>
                                    </div>
                                </div>
                            </Body>
                        </Card>
                    </div>
                ))}
            </div>
        </Container>
    );
};

const Impact: React.FC = () => {
    const { Body, Title, Text } = Card;

    const [impactData, setImpactData] = useState<ImpactResponseType | null>(null);

    const { data, loading, error, execute: fetchImpactData } = useApi(api.getImpactData.bind(api));

    useEffect(() => {
        fetchImpactData();
    }, []);

    useEffect(() => {
        if (data) {
            setImpactData(data);
        }
    }, [data]);

    // Show placeholder while loading
    if (loading) {
        return <ImpactPlaceholder />;
    }

    return (
        <Container>
            <SectionHeader
                autoFetch={true}
                dataType="header_6"
                withTitleAnimation={true}
                withDescriptionAnimation={false}
            />

            <div className='row d-flex align-items-center justify-content-start flex-wrap'>
                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-graduation-cap text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Universitas/Perguruan Tinggi/Sekolah Tinggi/Akademi']}</Title>
                                <Text as='p' className="text-gray-600">Universitas/Perguruan Tinggi/Sekolah Tinggi/Akademi</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-briefcase text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Swasta']}</Title>
                                <Text as='p' className="text-gray-600">
                                    Swasta
                                </Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-building-columns text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Publik/PNS/Kementerian/Lembaga']}</Title>
                                <Text as='p' className="text-gray-600">Publik/PNS/Kementerian</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-school text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Sekolah SD/SMP/SMA']}</Title>
                                <Text as='p' className="text-gray-600">SD/SMP/SMA/SMK</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-building text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_BUMN/BUMD']}</Title>
                                <Text as='p' className="text-gray-600">BUMN/BUMD</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-rocket text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Wirausaha/Entrepreneur']}</Title>
                                <Text as='p' className="text-gray-600">Wiraswasta/Entrepreneur</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`bi-people-fill text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_Masyarakat/LSM/NGO']}</Title>
                                <Text as='p' className="text-gray-600">LSM/NGO</Text>
                            </div>
                        </Body>
                    </Card>
                </div>

                <div className='col-md-3'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden mb-5' style={{ height: '120px' }}>
                        <Body className="d-flex align-items-center gap-3 p-4" style={{ height: '100%' }}>
                            <div className='d-flex align-items-center justify-content-center w-25'>
                                <span className={`feature-icon rounded-circle text-primary d-flex align-items-center justify-content-center icon-bounce` }>
                                    <i className={`fa-solid fa-shield-halved text-primary`} style={{ fontSize: '2.5rem' }}></i>
                                </span>
                            </div>
                            <div className='w-75'>
                                <Title as='h4' className="fw-bold mb-0 number-pulse">{impactData?.data?.['bidang_TNI/POLRI']}</Title>
                                <Text as='p' className="text-gray-600">TNI/POLRI</Text>
                            </div>
                        </Body>
                    </Card>
                </div>
            </div>
        </Container>
    );
};

export default Impact;